import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { createDateNodeView } from './DateNodeView';

export const DATE_NODE_NAME = 'date';

/** A numeric attribute off an HTML round-trip, or null. See the note on `signatureNode`. */
const numberAttribute = (element: HTMLElement, name: string): number | null => {
	const raw = element.getAttribute(name);
	if (raw === null || raw.trim() === '') {
		return null;
	}
	const value = Number(raw);
	return Number.isFinite(value) ? value : null;
};

export interface DateNodeOptions {
	/** Whether stamps render their metadata block, which sets the node's aspect ratio. */
	showStampMetadata?: boolean;
}

/**
 * The post-signing date box, a block atom like `SignatureNode`. `templateDatePlacementId` is the
 * id of the DATE `DocumentPlacement` row the save creates.
 */
export const DateNode = Node.create<DateNodeOptions>({
	name: DATE_NODE_NAME,
	group: 'block',
	atom: true,
	selectable: true,
	draggable: true,

	addOptions() {
		return {};
	},

	addAttributes() {
		return {
			templateDatePlacementId: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-date-placement-id'),
				renderHTML: (attributes) => ({
					'data-date-placement-id': attributes.templateDatePlacementId,
				}),
			},
			widthMm: {
				default: null,
				parseHTML: (element) => numberAttribute(element, 'data-width-mm'),
				renderHTML: (attributes) =>
					attributes.widthMm === null || attributes.widthMm === undefined
						? {}
						: { 'data-width-mm': String(attributes.widthMm) },
			},
			offsetXMm: {
				default: null,
				parseHTML: (element) => numberAttribute(element, 'data-offset-x-mm'),
				renderHTML: (attributes) =>
					attributes.offsetXMm === null || attributes.offsetXMm === undefined
						? {}
						: { 'data-offset-x-mm': String(attributes.offsetXMm) },
			},
		};
	},

	parseHTML() {
		return [{ tag: 'div[data-date-slot]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return ['div', mergeAttributes(HTMLAttributes, { 'data-date-slot': '', class: 'ds-date' })];
	},

	renderText() {
		return '';
	},

	addNodeView() {
		return ReactNodeViewRenderer(createDateNodeView(this.options));
	},
});
